import MapBaseLayer from '@alltrails/analytics/enums/MapBaseLayer';
import MapLocation from '@alltrails/analytics/enums/MapLocation';
import MapOverlay from '@alltrails/analytics/enums/MapOverlay';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import SearchItemType from '@alltrails/analytics/enums/SearchItemType';
import SearchSortFilter from '@alltrails/analytics/enums/SearchSortFilter';
import { pathToRegexp } from 'path-to-regexp';
import Coupon from 'types/Coupon';
import Plan from 'types/Plan';
import RecurlyPlan from 'types/RecurlyPlan';
import { AlgoliaResultType } from 'types/Search/Algolia';
import { Sort } from 'types/Search/Sort';
import { isTrial } from './PlanUtils';

const typeMapping = {
  [AlgoliaResultType.Trail]: SearchItemType.Trail,
  [AlgoliaResultType.Track]: SearchItemType.Track,
  [AlgoliaResultType.Place]: SearchItemType.Place,
  [AlgoliaResultType.List]: SearchItemType.List,
  [AlgoliaResultType.Area]: SearchItemType.Area,
  [AlgoliaResultType.Map]: SearchItemType.Map,
  [AlgoliaResultType.Filter]: SearchItemType.Attribute,

  // TODO DISCO-604 map to the actual Amplitude analytics when ready.
  [AlgoliaResultType.POI]: AlgoliaResultType.POI as unknown as SearchItemType
};

const sortMapping = {
  [Sort.BEST_MATCH]: SearchSortFilter.BestMatch,
  [Sort.CLOSEST]: SearchSortFilter.Closest,
  [Sort.MOST_POPULAR]: SearchSortFilter.MostPopular,
  [Sort.NEWLY_ADDED]: SearchSortFilter.NewlyAdded,

  // These aren't tracked in Amplitude today. Just return the original sort value cast as an Amplitude value.
  [Sort.NAME]: Sort.NAME as unknown as SearchSortFilter,
  [Sort.LIST_ORDER]: Sort.LIST_ORDER as unknown as SearchSortFilter,
  [Sort.DATE]: Sort.DATE as unknown as SearchSortFilter,
  [Sort.RECENTLY_ADDED]: Sort.RECENTLY_ADDED as unknown as SearchSortFilter
};

function algoliaTypeToAmplitudeType(algoliaResultType: AlgoliaResultType): SearchItemType {
  return typeMapping[algoliaResultType];
}

function sortToAmplitudeSort(sort: Sort): SearchSortFilter {
  return sortMapping[sort];
}

function getPurchaseScreenTypePlan(plan: Plan, coupon?: Coupon): PurchaseScreenTypePlan {
  if (coupon) {
    return PurchaseScreenTypePlan.Promo;
  }

  if (isTrial(plan)) {
    return PurchaseScreenTypePlan.FreeTrial;
  }

  return PurchaseScreenTypePlan.NonTrial;
}

// Based on getPageTitle from RoutePath.js
function getMapLocation(url: string): MapLocation {
  if (pathToRegexp('/:locale?/trail/(.*)').test(url) || pathToRegexp('/:locale?/explore/trail/(.*)').test(url)) {
    return MapLocation.Trail;
  }

  if (pathToRegexp('/:locale?/members/:slug/recordings').test(url) || pathToRegexp('/:locale?/explore/recording/(.*)').test(url)) {
    return MapLocation.Activity;
  }

  if (pathToRegexp('/:locale?/members/:slug/maps').test(url) || pathToRegexp('/:locale?/explore/map/(.*)').test(url)) {
    return MapLocation.SavedMap;
  }

  if (pathToRegexp('/:locale?/community(.*)').test(url)) {
    return MapLocation.Community;
  }

  return MapLocation.Explore;
}

function getMapBaseLayer(
  key: 'alltrailsOutdoorsV2' | 'alltrailsSatellite' | 'arcgisSatellite' | 'opencycle' | 'openstreet' | 'roadmap' | 'topo' | 'worldparks' | 'worldtopo'
): MapBaseLayer {
  switch (key) {
    case 'alltrailsOutdoorsV2':
      return MapBaseLayer.Alltrailsv2;
    case 'alltrailsSatellite':
    case 'arcgisSatellite':
      return MapBaseLayer.Satellite;
    case 'opencycle':
      return MapBaseLayer.Ocm;
    case 'openstreet':
      return MapBaseLayer.Osm;
    case 'roadmap':
      return MapBaseLayer.Road;
    case 'topo':
      return MapBaseLayer.Topo;
    case 'worldparks':
      return MapBaseLayer.Worldparks;
    case 'worldtopo':
      return MapBaseLayer.Worldtopo;
    default:
      return MapBaseLayer.Alltrailsv2;
  }
}

function getMapOverlay(
  key: 'airQuality' | 'heatmap' | 'lightPollution' | 'nearbyTrails' | 'photos' | 'pollen' | 'waypoints' | 'weather'
): MapOverlay {
  switch (key) {
    case 'airQuality':
      return MapOverlay.Airquality;
    case 'heatmap':
      return MapOverlay.Heatmap;
    case 'lightPollution':
      return MapOverlay.LightPollution;
    case 'nearbyTrails':
      return MapOverlay.NearbyTrails;
    case 'photos':
      return MapOverlay.Photos;
    case 'pollen':
      return MapOverlay.Pollen;
    case 'waypoints':
      return MapOverlay.Waypoints;
    case 'weather':
      return MapOverlay.Weather;
    default:
      return '' as MapOverlay;
  }
}

export { algoliaTypeToAmplitudeType, getPurchaseScreenTypePlan, sortToAmplitudeSort, getMapLocation, getMapBaseLayer, getMapOverlay };
